import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_label } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_label';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EKeyCode } from '@/enums/e_key_code';
import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_hint_shortcut } from '@/hooks/hints_shortcut/use_hint_shortcut';
import { use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { HOOK_WORKSPACE__get_list_sorted } from '@/hooks/workspace/HOOK_WORKSPACE__get_list_sorted';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { WorkspaceAvatarReadOnly } from '@/pages/+settings/workspace/_base/WorkspaceAvatarReadOnly';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { cn } from '@/utils/utils_client';
import { set_current_workspace } from '@/utils/utils_client_workspace_child_entity_viewers';
import { DTO__workspace } from '@shared/dto/+workspace/workspace_entity/DTO__workspace';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewShortcutTrigger } from '../../shortcut_hint/ViewShortcutTrigger';
import { WatcherShortcutWorkspaceNavigation } from '../../watchers/workspace/watcher_shortcut_workspace_navigation';
import { ButtonWorkspaceCreate } from './ButtonWorkspaceCreate';
import { ButtonWorkspaceGoToDefault } from './ButtonWorkspaceGoToDefault';
import { ButtonWorkspaceSetDefault } from './ButtonWorkspaceSetDefault';
import { IconWorkspaceDefault } from './IconWorkspaceDefault';

export function DropdownWorkspaceAvatar() {
  /*
   * FIELDS
   */
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const { on_user_uses_component, on_user_uses_shortcut } = use_hint_shortcut(EShortcutType.NAVIGATION_HOTKEYS_BETWEEN_WORKSPACES);

  const { is_authorized } = use_slice_user(['is_authorized']);
  const { workspace_current, workspaces_map, all_workspaces_users_map } = use_slice_workspace([
    'workspace_current',
    'workspaces_map',
    'all_workspaces_users_map',
  ]);

  const [is_open, set_is_open] = useState(false);
  const ref_dropdown_item_workspace = use_ref_with_fallback(ENUM__html_ref_stub.DIV);

  const workspaces_list_sorted = HOOK_WORKSPACE__get_list_sorted();

  /*
   * EVENTS
   */

  function on_workspace_selected(workspace_id: string, is_used_shortcut: boolean = false) {
    if (workspace_current._id == workspace_id) return;

    is_used_shortcut ? on_user_uses_shortcut() : on_user_uses_component();

    set_current_workspace(workspace_id);
  }

  function onMouseEnter() {
    if (!is_authorized) return;

    set_is_open(true);
  }

  /*
   * METHODS
   */

  /*
   * RENDERS
   */

  function render_shortcuts() {
    if (workspaces_list_sorted.is_empty()) return null;

    let out = [];

    let index_max = Math.min(workspaces_list_sorted.length - 1, 9);

    for (let i = 0; i <= index_max; i++) {
      let key_number = i + 1;

      let workspace = workspaces_list_sorted[i];

      let key_code = `digit${key_number}` as EKeyCode;

      let shortcut = (
        <WatcherShortcutWorkspaceNavigation key={`shortcut_workspace_${i}`} {...{ key_code, on_workspace_selected }} workspace_id={workspace._id} />
      );

      out.push(shortcut);
    }

    return out;
  }

  function render_item(workspace: DTO__workspace, index: number) {
    let number = index + 1;

    if (number == 10) number = 0;

    return (
      <UI_PRIMITIVE__dropdown_menu_item
        key={workspace._id}
        ref={workspace._id === workspace_current._id ? ref_dropdown_item_workspace : null}
        {...{ on_item_selected: on_workspace_selected }}
        class_name_hovered_selected="bg-neutral-100 dark:bg-neutral-800"
        values_selected={[workspace_current._id]}
        value={workspace._id}
        className={cn('gap-2 p-2 cursor-pointer text-bar-foreground')}
      >
        <WorkspaceAvatarReadOnly className="!size-8" {...{ workspace }}>
          <ButtonWorkspaceSetDefault {...{ workspace }} />
          <IconWorkspaceDefault {...{ workspace }} />
        </WorkspaceAvatarReadOnly>
        {number}: {workspace.name}
      </UI_PRIMITIVE__dropdown_menu_item>
    );
  }

  /*
   * HTML
   */

  return (
    <>
      {render_shortcuts()}
      <ViewShortcutTrigger hint_shortcut_keys={[EShortcutType.NAVIGATION_HOTKEYS_BETWEEN_WORKSPACES]}>
        <UI_PRIMITIVE__dropdown_menu modal={false} open={is_open} onOpenChange={() => set_is_open(false)}>
          <UI_PRIMITIVE__dropdown_menu_trigger className="mb-3" asChild {...{ onMouseEnter }}>
            <WorkspaceAvatarReadOnly className="!size-[3rem]" workspace={workspace_current}>
              <ButtonWorkspaceGoToDefault />
            </WorkspaceAvatarReadOnly>
          </UI_PRIMITIVE__dropdown_menu_trigger>
          <UI_PRIMITIVE__dropdown_menu_content
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            onMouseLeave={() => set_is_open(false)}
            align="start"
            side="right"
            sideOffset={4}
          >
            <UI_PRIMITIVE__dropdown_menu_label className="text-xs text-muted-foreground">
              {t(i18n_base_ui.sidebar_navigation.workspaces.title)}
            </UI_PRIMITIVE__dropdown_menu_label>
            <ScrollArea
              viewportClassName="max-h-96"
              style={{ maskImage: 'linear-gradient(to top, transparent 0%, rgba(0, 0, 0, 1.0) 2%, rgba(0, 0, 0, 1.0) 98%, transparent 100%)' }}
            >
              {workspaces_list_sorted.map(render_item)}
            </ScrollArea>
            <ButtonWorkspaceCreate {...{ ref_dropdown_item_workspace }} />
          </UI_PRIMITIVE__dropdown_menu_content>
        </UI_PRIMITIVE__dropdown_menu>
      </ViewShortcutTrigger>
    </>
  );
}
